/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Aegis AO Soft LLC and Alexander Orlov.
 *
 * This code may be used, reproduced, modified, or distributed ONLY with the
 * prior written permission of Aegis AO Soft LLC / Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 */


/*
 * EditOwnerProfileModal — profile editor for the logged-in owner.
 *
 * Owners have very different properties from drivers (company, primary business
 * location, no driver-license workflow), so this modal mirrors the owner
 * registration wizard (CreateOwnerPage → PersonalInfoStep + PrimaryLocationStep)
 * instead of reusing the driver-license–centric EditUserDetailsModal.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { MapPin, Save } from 'lucide-react';
import { Modal } from '@/components/common/Modal';
import { Button } from '@/components/common';
import { usersApi } from '@/api';
import { useAuthStore } from '@/store';
import type { UserAuthenticationResult } from '@/types';
import { PersonalInfoStep } from '@/pages/create-user-and-agreement/PersonalInfoStep';
import { initialFormData } from '@/pages/create-user-and-agreement/types';
import type { DriverFormData } from '@/pages/create-user-and-agreement/types';
import { isValidEmail } from '@/utils';

interface EditOwnerProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: UserAuthenticationResult | null;
}

// Normalize any backend date string to the YYYY-MM-DD a <input type="date"> / the
// wizard's DOB selects expect.
const toDateInput = (dateStr: string | undefined | null): string => {
  if (!dateStr) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
};

export const EditOwnerProfileModal: React.FC<EditOwnerProfileModalProps> = ({
  isOpen,
  onClose,
  user,
}) => {
  const { t } = useTranslation();
  const { patchUser } = useAuthStore();

  const [formData, setFormData] = useState<DriverFormData>({ ...initialFormData });
  const [saving, setSaving] = useState(false);
  // Display-only; SMS consent is managed by Twilio (STOP/START), matching the wizard.
  const [smsConsent, setSmsConsent] = useState(true);

  // PersonalInfoStep needs collapsible-section state even though those sections
  // are hidden here (license + address).
  const [licenseExpanded, setLicenseExpanded] = useState(false);
  const [addressExpanded, setAddressExpanded] = useState(false);

  const addressInput =
    'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

  // Prefill from the authenticated owner each time the modal opens.
  useEffect(() => {
    if (!isOpen || !user) return;
    setFormData({
      ...initialFormData,
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      middleName: user.middleName || '',
      companyName: user.company || '',
      email: user.email || '',
      phoneNumber: user.phone || '',
      dateOfBirth: toDateInput(user.birthDate),
      addressLine1: user.addressLine1 || '',
      addressLine2: user.addressLine2 || '',
      city: user.city || '',
      state: (user.stateId || user.state || '').toUpperCase(),
      zipCode: user.zipCode || '',
    });
  }, [isOpen, user]);

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const emailValid = isValidEmail(formData.email);
  const phoneDigits = (formData.phoneNumber || '').replace(/\D/g, '');
  const canSave = useMemo(
    () =>
      !!formData.firstName.trim() &&
      !!formData.lastName.trim() &&
      emailValid &&
      phoneDigits.length >= 10,
    [formData.firstName, formData.lastName, emailValid, phoneDigits.length]
  );

  const handleSave = async () => {
    if (!user) return;
    if (!formData.firstName.trim()) { toast.error(t('wizard.validation.firstName', 'First name is required')); return; }
    if (!formData.lastName.trim()) { toast.error(t('wizard.validation.lastName', 'Last name is required')); return; }
    if (!emailValid) { toast.error(t('wizard.validation.emailInvalid', 'Please enter a valid email address')); return; }
    if (phoneDigits.length < 10) { toast.error(t('wizard.validation.phoneInvalid', 'Please enter a valid phone number')); return; }

    const newEmail = formData.email.trim().toLowerCase();
    setSaving(true);
    try {
      await usersApi.updateOwnerProfile(user.id, user.email, {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        middleName: formData.middleName.trim(),
        company: formData.companyName.trim(),
        email: newEmail,
        phone: formData.phoneNumber.trim(),
        dateOfBirth: formData.dateOfBirth || undefined,
        addressLine1: formData.addressLine1.trim(),
        addressLine2: formData.addressLine2.trim(),
        city: formData.city.trim(),
        state: formData.state.trim().toUpperCase(),
        zipCode: formData.zipCode.trim(),
      });

      // Keep the persisted auth user (sidebar header, etc.) in sync.
      patchUser({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        middleName: formData.middleName.trim(),
        company: formData.companyName.trim(),
        // Clearing the company field falls the shell header back to the personal name, so the
        // company flag has to follow it down as well.
        isCompany: !!formData.companyName.trim(),
        email: newEmail,
        phone: formData.phoneNumber.trim(),
        birthDate: formData.dateOfBirth || undefined,
        addressLine1: formData.addressLine1.trim(),
        addressLine2: formData.addressLine2.trim(),
        city: formData.city.trim(),
        state: formData.state.trim().toUpperCase(),
        stateId: formData.state.trim().toUpperCase(),
        zipCode: formData.zipCode.trim(),
      });

      toast.success(t('stripeCards.profileSaved', 'Profile updated successfully'));
      onClose();
    } catch (error: any) {
      // Backend returns 409 when the new email already belongs to another account.
      if (error?.response?.status === 409) {
        toast.error(error.response.data?.message || t('usersPage.duplicateEmail', 'This email is already in use by another account.'), { duration: 6000 });
      } else {
        toast.error(t('stripeCards.profileSaveError', 'Failed to save profile'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('ownerProfile.editTitle', 'Edit Owner Profile')}
      size="2xl"
    >
      {user && (
        <div className="space-y-6">
          {/* Owner header */}
          <div className="text-center">
            <h3 className="text-xl font-semibold text-gray-900">
              {formData.companyName?.trim() || `${user.firstName} ${user.lastName}`.trim()}
            </h3>
            <p className="text-sm text-gray-600 mt-1">{user.email}</p>
          </div>

          {/* Owner Information — reuses the exact wizard step (company + personal
              details), with the driver-license and inline-address sections hidden. */}
          <PersonalInfoStep
            formData={formData}
            setFormData={setFormData}
            smsConsent={smsConsent}
            setSmsConsent={setSmsConsent}
            licenseAnalysisSuccess={false}
            isLicenseInfoExpanded={licenseExpanded}
            setIsLicenseInfoExpanded={setLicenseExpanded}
            isAddressInfoExpanded={addressExpanded}
            setIsAddressInfoExpanded={setAddressExpanded}
            onNext={() => {}}
            onPrevious={() => {}}
            hideNavButtons
            showCompanyName
            hideLicenseInfo
            hideAddressInfo
            infoTitle={t('wizard.owner.infoTitle', 'Owner Information')}
          />

          {/* Primary Location — mirrors PrimaryLocationStep field layout. */}
          <div className="space-y-3">
            <h4 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-blue-500" /> {t('wizard.location.title', 'Primary Location')}
            </h4>
            <div className="p-3 sm:p-4 bg-gray-50 rounded-lg space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('wizard.license.addressLine1', 'Address Line 1')}</label>
                <input type="text" name="addressLine1" autoComplete="address-line1" value={formData.addressLine1} onChange={handleAddressChange} className={addressInput} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('wizard.license.addressLine2', 'Address Line 2')}</label>
                <input type="text" name="addressLine2" autoComplete="address-line2" value={formData.addressLine2} onChange={handleAddressChange} className={addressInput} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('wizard.license.city', 'City')}</label>
                  <input type="text" name="city" autoComplete="address-level2" value={formData.city} onChange={handleAddressChange} className={addressInput} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('wizard.license.state', 'State')}</label>
                  <input type="text" name="state" autoComplete="address-level1" value={formData.state} onChange={handleAddressChange} maxLength={2}
                    className={`${addressInput} uppercase`} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('wizard.license.zipCode', 'ZIP Code')}</label>
                  <input type="text" name="zipCode" autoComplete="postal-code" value={formData.zipCode} onChange={handleAddressChange} className={addressInput} />
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button onClick={handleSave} disabled={saving || !canSave}>
              <Save className="h-4 w-4 mr-1" />
              {saving ? t('common.saving', 'Saving...') : t('common.saveChanges', 'Save Changes')}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default EditOwnerProfileModal;
